// Store & Warehouse → "Locations" (admin, kitchen flow lane C): add, rename,
// switch off, and move "receives deliveries" / "sales draw from here". The
// server refuses switching off a location that still holds stock, and never
// lets In Transit change.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { T } from '@/lib/inventoryFlow/strings'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { intentScope, useIntentKey } from '@/lib/inventoryFlow/intentKey'
import type { FlowLocation } from '@/lib/inventoryFlow/types'

const KINDS = ['store', 'kitchen', 'bar', 'other'] as const

export function LocationsCard({ showToast }: { showToast: (type: 'success' | 'error', message: string) => void }) {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['inventory-flow', 'locations'], queryFn: () => apiClient.getFlowLocations() })
  const locations = data?.data ?? []
  const [name, setName] = useState('')
  const [kind, setKind] = useState<(typeof KINDS)[number]>('store')
  const [editing, setEditing] = useState<Record<string, string>>({})

  const done = (msg: string) => {
    showToast('success', msg)
    void qc.invalidateQueries({ queryKey: ['inventory-flow'] })
  }
  const fail = (e: unknown) => showToast('error', e instanceof Error ? e.message : 'Something went wrong')

  // One key per new place (name + kind), reused if the save is retried.
  const createIntent = useIntentKey(intentScope('create-location', name.trim(), kind))
  const create = useMutation({
    mutationFn: () => apiClient.createFlowLocation({ name: name.trim(), kind }, createIntent.key),
    onSuccess: () => { createIntent.renew(); setName(''); done(T.saved) },
    onError: fail,
  })
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof apiClient.updateFlowLocation>[1] }) =>
      apiClient.updateFlowLocation(v.id, v.body, newIdempotencyKey()),
    onSuccess: () => done(T.saved),
    onError: fail,
  })

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div>
          <h2 className="text-lg font-semibold">{T.locationsTitle}</h2>
          <p className="text-sm text-muted-foreground">{T.locationsHelp}</p>
        </div>
        <ul className="divide-y rounded-lg border">
          {locations.map((l: FlowLocation) => {
            const transit = l.kind === 'transit'
            const draft = editing[l.id]
            return (
              <li key={l.id} className="flex flex-wrap items-center gap-3 p-3">
                <div className="min-w-[12rem] flex-1 space-y-1">
                  {draft != null ? (
                    <div className="flex gap-2">
                      <Input className="h-10" value={draft} onChange={(e) => setEditing((s) => ({ ...s, [l.id]: e.target.value }))} aria-label={T.locationName} />
                      <Button className="h-10" disabled={!draft.trim()} onClick={() => { patch.mutate({ id: l.id, body: { name: draft.trim() } }); setEditing((s) => { const n = { ...s }; delete n[l.id]; return n }) }}>Save</Button>
                    </div>
                  ) : (
                    <button type="button" disabled={transit} className="text-left font-semibold disabled:cursor-default"
                      onClick={() => setEditing((s) => ({ ...s, [l.id]: l.name }))}>
                      {l.name} <span className="text-xs font-normal text-muted-foreground">({l.kind}{l.is_active ? '' : ', off'})</span>
                    </button>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {l.is_receiving_default && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">{T.receivesDeliveries}</span>}
                    {l.is_sale_source && <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-900 dark:bg-blue-950 dark:text-blue-200">{T.salesDrawFrom}</span>}
                    {l.holds_stock && <span>{T.holdsStock(l.items_held)}</span>}
                  </div>
                </div>
                {!transit && (
                  <div className="flex flex-wrap gap-2">
                    {!l.is_receiving_default && l.is_active && (
                      <Button size="sm" variant="outline" onClick={() => patch.mutate({ id: l.id, body: { is_receiving_default: true } })}>{T.makeReceiving}</Button>
                    )}
                    {!l.is_sale_source && l.is_active && (
                      <Button size="sm" variant="outline" onClick={() => patch.mutate({ id: l.id, body: { is_sale_source: true } })}>{T.makeSaleSource}</Button>
                    )}
                    {!l.is_receiving_default && !l.is_sale_source && (
                      <Button size="sm" variant="ghost" onClick={() => patch.mutate({ id: l.id, body: { is_active: !l.is_active } })}>
                        {l.is_active ? T.switchOff : T.switchOn}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <Input className="max-w-xs" placeholder={T.locationName} value={name} onChange={(e) => setName(e.target.value)} aria-label={T.locationName} />
          <select className="h-10 rounded-md border bg-background px-3" value={kind} onChange={(e) => setKind(e.target.value as (typeof KINDS)[number])} aria-label={T.locationKind}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>{T.addLocation}</Button>
        </div>
      </CardContent>
    </Card>
  )
}
